/**
 * CLT Synapse — Web Push helpers.
 * Desktop browsers only: Chrome / Firefox / Edge / Safari macOS.
 *
 * Notifications are **ON by default** for all users. autoEnablePush() is
 * called on app boot:
 *   - If browser support is missing → no-op.
 *   - If user explicitly disabled in Settings → no-op (respects the flag).
 *   - If permission is `default` → request it; on grant, subscribe + POST.
 *   - If permission is `granted` → make sure we have an active subscription.
 *   - If permission is `denied` → no-op (user must re-allow in browser UI).
 */
import { apiClient } from '@/lib/api';
import logger from '@/lib/logger';

const SW_URL = '/sw.js';
const USER_DISABLED_KEY = 'clt_push_user_disabled';

export const isPushSupported = () =>
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

export const isUserDisabled = () =>
    typeof localStorage !== 'undefined' && localStorage.getItem(USER_DISABLED_KEY) === '1';

export const setUserDisabled = (val) => {
    if (typeof localStorage === 'undefined') return;
    if (val) localStorage.setItem(USER_DISABLED_KEY, '1');
    else localStorage.removeItem(USER_DISABLED_KEY);
};

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
}

let _swRegistrationPromise = null;
export function registerServiceWorker() {
    if (!isPushSupported()) return Promise.resolve(null);
    if (_swRegistrationPromise) return _swRegistrationPromise;
    _swRegistrationPromise = navigator.serviceWorker.register(SW_URL).catch((e) => {
        logger.error('SW registration failed', e);
        _swRegistrationPromise = null;
        return null;
    });
    return _swRegistrationPromise;
}

export async function getPushState() {
    if (!isPushSupported()) {
        return { supported: false, permission: 'default', subscribed: false, userDisabled: false };
    }
    const permission = Notification.permission;
    let subscribed = false;
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        subscribed = !!sub;
    } catch (e) {
        logger.error('getPushState SW ready failed', e);
    }
    return { supported: true, permission, subscribed, userDisabled: isUserDisabled() };
}

/**
 * Subscribe to push and save the subscription on the server.
 * Assumes permission is already granted. Idempotent.
 */
async function subscribeAndSave() {
    await registerServiceWorker();
    const reg = await navigator.serviceWorker.ready;
    const { data: { public_key } } = await apiClient.get('/push/vapid-public-key');
    if (!public_key) throw new Error('Server is missing VAPID key');

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(public_key),
        });
    }
    const json = sub.toJSON();
    await apiClient.post('/push/subscribe', {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
    });
    return true;
}

/**
 * Auto-enable push on app boot. Safe to call multiple times — it's
 * idempotent and rate-limited by the browser anyway.
 */
export async function autoEnablePush() {
    if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
    if (isUserDisabled()) return { ok: false, reason: 'user-disabled' };

    let permission = Notification.permission;
    if (permission === 'denied') return { ok: false, reason: 'denied' };

    if (permission === 'default') {
        try {
            permission = await Notification.requestPermission();
        } catch (e) {
            logger.error('requestPermission failed', e);
            return { ok: false, reason: 'request-failed' };
        }
        if (permission !== 'granted') return { ok: false, reason: permission };
    }

    try {
        await subscribeAndSave();
        return { ok: true };
    } catch (e) {
        logger.error('subscribeAndSave failed', e);
        return { ok: false, reason: 'subscribe-failed', error: e };
    }
}

/**
 * Settings toggle — called when the user flips the switch in /settings.
 * - `enable=true`: clear the user-disabled flag and try to re-subscribe.
 *   If permission was previously denied, returns ok=false so the UI can
 *   tell the user to unblock in browser settings.
 * - `enable=false`: persist the disabled flag, unsubscribe locally + on server.
 */
export async function setPushEnabled(enable) {
    if (!isPushSupported()) throw new Error('Push not supported in this browser');

    if (enable) {
        setUserDisabled(false);
        let permission = Notification.permission;
        if (permission === 'default') {
            permission = await Notification.requestPermission();
        }
        if (permission !== 'granted') {
            throw new Error(
                permission === 'denied'
                    ? 'Notifications are blocked. Click the lock icon in your address bar to allow them.'
                    : 'Notifications permission not granted.'
            );
        }
        await subscribeAndSave();
        return true;
    }

    // Disable
    setUserDisabled(true);
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
            const endpoint = sub.endpoint;
            await sub.unsubscribe();
            await apiClient.post('/push/unsubscribe', { endpoint }).catch(() => {});
        }
    } catch (e) {
        logger.error('disable push failed', e);
    }
    return false;
}

export async function sendPushTest() {
    return apiClient.post('/push/test');
}
